import React from "react";
import Tippy from "@tippyjs/react/headless";
import camelCase from "lodash/camelCase";
import { cls } from "@wss/cls";
import FilterListItems from "./FilterListItems";
import { SectionGroup } from "./Filters";
import { GroupProps } from "./GroupProps";
import useIsMounted from "~/common/useIsMounted";
import StandardIcon from "~/common/Icons/Standard";
import Arrow from "~/common/PopupArrow";

interface Props {
  group: SectionGroup;
  toggleCollapsed: GroupProps["toggleCollapsed"];
  filterText?: string;
  useGroupNameInUrl?: boolean;
  groupsWithMultipleSelections: number;
  prefetch?: boolean;
  pageUrl?: string;
}

const collapsibleFilterButtonClasses = [
  "bg-white",
  "border-gray-200",
  "border-solid",
  "border-0",
  "border-t",
  "block",
  "font-semibold",
  "text-base-1/2",
  "mt-0",
  "pl-2",
  "pr-3",
  "pt-1-1/2",
  "pb-0.5",
  "lt:py-0",
  "sticky",
  "-top-[1px]",
  "text-left",
  "text-gray-800",
  "w-full",
  "lt:font-bold",
  "lt:text-sm-1/2",
  "lt:relative",
  "leading-6",
  "align-middle",
  "focus:outline-offset-[-7px]",
  "z-10",
].join(" ");

const FilterGroup = (props: Props) => {
  const { group, toggleCollapsed, filterText = "filter", pageUrl } = props;

  const hasHelpText = !!group.groupDescription;
  return (
    <li className="m-0 p-0 border-0">
      <fieldset
        className="border-solid border-0 p-0 mx-0"
        data-testid={filterText}
        key={`${filterText}-${group.groupNameEncoded}`}
        id={`${camelCase(group.groupName)}Filter`}
        aria-label={group.groupName}
      >
        <FilterCategory group={group} toggleCollapsed={toggleCollapsed}>
          <span
            className={cls(
              "filter-list__content",
              hasHelpText ? "lt:flex lt:items-center" : "",
            )}
          >
            {group.groupName}{" "}
            {hasHelpText && (
              <StandardIcon
                name="question"
                data-testid="questionmark"
                className="hidden h-3 ml-1 opacity-50 w-3 lt:block"
              />
            )}
          </span>
          <StandardIcon
            name="chevron-open"
            style={{
              right: 12,
              color: "#ccc",
              top: "50%",
            }}
            className={cls(
              "h-2",
              "absolute",
              "fill-current",
              "w-2",
              "transform",
              "-translate-y-1/2",
              "transition-all",
              "ease-in",
              "duration-300",
              group.collapsed && "rotate-180",
            )}
          />
        </FilterCategory>

        <FilterListContainer
          groupName={group.groupName}
          collapsed={group.collapsed}
        >
          <FilterListItems
            {...props}
            filterText={filterText}
            pageUrl={pageUrl}
          />
        </FilterListContainer>
      </fieldset>
    </li>
  );
};

interface FilterButtonProps {
  group: SectionGroup;
  toggleCollapsed: GroupProps["toggleCollapsed"];
  children: React.ReactNode;
}

function CategoryButton({
  group,
  toggleCollapsed,
  children,
}: FilterButtonProps) {
  return (
    <button
      type="button"
      data-no-jquery
      data-content={group.groupDescription}
      className={cls(
        group.collapsed && "collapsed mb-px",
        collapsibleFilterButtonClasses,
      )}
      onClick={() => toggleCollapsed(group)}
      aria-expanded={!group.collapsed}
    >
      {children}
    </button>
  );
}

function FilterCategory({
  group,
  toggleCollapsed,
  children,
}: FilterButtonProps) {
  const isMounted = useIsMounted();
  const hasHelpText = !!group.groupDescription;

  if (isMounted && hasHelpText) {
    return (
      <Tippy
        placement="right"
        hideOnClick={false}
        render={(attrs) => (
          <div className="relative left-5px">
            <div
              className="bg-clip-padding bg-white border-black-200 rounded-md border-solid border hidden max-w-full p-px whitespace-normal w-48-1/2 shadow-black-200 lt:block"
              {...attrs}
            >
              <div className="text-sm-1/2 leading-4-1/4 px-3-1/2 py-2-1/4 relative z-20">
                <div
                  dangerouslySetInnerHTML={{
                    __html: group.groupDescription
                      ? group.groupDescription
                      : "",
                  }}
                />
              </div>
              {attrs["data-placement"] && (
                <Arrow placement={attrs["data-placement"]} />
              )}
            </div>
          </div>
        )}
      >
        <button
          className={cls(
            group.collapsed ? "collapsed" : "z-10",
            collapsibleFilterButtonClasses,
          )}
          onClick={() => toggleCollapsed(group)}
          type="button"
        >
          {children}
        </button>
      </Tippy>
    );
  }
  return (
    <CategoryButton group={group} toggleCollapsed={toggleCollapsed}>
      {children}
    </CategoryButton>
  );
}

interface FilterListContainerProps {
  children: React.ReactNode;
  collapsed?: boolean;
  groupName: string;
}

export const collapsedStyles =
  "hidden h-0 overflow-hidden relative transition-all ease-in-out duration-300";

function FilterListContainer({
  children,
  collapsed,
  groupName,
}: FilterListContainerProps) {
  if (
    groupName.toLowerCase() === "color" ||
    groupName.toLowerCase() === "handle color" ||
    groupName.toLowerCase() === "frame color" ||
    groupName.toLowerCase() === "seat color" ||
    groupName.toLowerCase() === "back color"
  ) {
    return (
      <ul
        className={cls(
          "bg-white",
          "flex",
          "flex-row",
          "flex-wrap",
          "px-2-1/2",
          "m-0",
          "pr-1.5",
          "lt:py-2",
          "lt:pr-1.5",
          collapsed ? cls("py-0", collapsedStyles) : "py-2-1/2",
        )}
      >
        {children}
      </ul>
    );
  }
  return (
    <ul
      className={
        collapsed
          ? collapsedStyles
          : "bg-white pl-4-1/2 m-0 lt:bg-transparent lt:mt-0 lt:pl-0 pb-2 lt:py-2"
      }
    >
      {children}
    </ul>
  );
}

export default FilterGroup;
